import { BMInputField } from "@traveloka/district-bloom";
import { ComponentProps, useEffect, useState } from "react";
import {
  NativeSyntheticEvent,
  StyleSheet,
  TextInputFocusEventData,
} from "react-native";
import { useMobileContentResource } from "../../core/resource/MobileResourceProvider";
import {
  fixedDateStringCombination,
  formateDateToStringTemplate,
  parseStringTemplateToDate,
  transformDateInput,
} from "./helper";

export type BMDateInputFieldProps = Omit<
  ComponentProps<typeof BMInputField>,
  | "onChange"
  | "onChangeText"
  | "value"
  | "variant"
  | "keyboardType"
  | "placeholder"
  | "selection"
  | "onDropdownToggle"
> & {
  value: Date | null;
  onChange?: (value: Date | null) => void;
};

export default function BMDateInputField({
  error: initialError,
  value: initialValue = null,
  onChange,
  onBlur,
  ...rest
}: BMDateInputFieldProps) {
  const [inputValue, setInputValue] = useState(
    initialValue ? formateDateToStringTemplate(initialValue) : ""
  );
  const [selection, setSelection] = useState({ start: 0, end: 0 });
  const [error, setError] = useState<string | undefined>(undefined);

  const { CorporateGeneralErrorMessage: cr } = useMobileContentResource();

  function handleOnChange(text: string) {
    const data = transformDateInput(text, inputValue, selection);
    const { result, cursor, error: isError } = data;

    setInputValue(result);
    setSelection(cursor);
    setError(isError ? cr.dateInputInvalidFormat : undefined);

    const date = parseStringTemplateToDate(result);
    const cleaned = result.replace(/\D/g, "");

    if (!isError && cleaned.length === 8 && !isNaN(date.getTime())) {
      onChange?.(date);
    } else {
      onChange?.(null);
    }
  }

  function handleOnBlur(e: NativeSyntheticEvent<TextInputFocusEventData>) {
    const { fixed, isValid } = fixedDateStringCombination(inputValue);
    const cleaned = fixed.replace(/\D/g, "");
    onBlur?.(e);

    if (!isValid) {
      setInputValue(fixed);
      setError(undefined);
    }

    if (cleaned.length > 0 && cleaned.length < 8) {
      setError(cr.dateInputInvalidFormat);
    }
  }

  useEffect(() => {
    if (!initialValue) return;
    const formattedDate = formateDateToStringTemplate(initialValue);
    setInputValue(formattedDate);
    setSelection({ start: 14, end: formattedDate.length });
  }, [initialValue]);

  return (
    <BMInputField
      {...rest}
      error={error || initialError}
      value={inputValue}
      selection={selection}
      keyboardType="number-pad"
      placeholder="dd - mm - yyyy"
      onChangeText={handleOnChange}
      onBlur={handleOnBlur}
      returnKeyType="done"
    />
  );
}

const styles = StyleSheet.create({});
